import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import API from "api.js";
import { setAlert } from 'action/index.js';
import { store } from 'store/index';
import { isValidPhoneNumber } from "libphonenumber-js/mobile";
import CustomPhoneInput from "../components/PublicPagesReusable/CustomPhoneInput";
import ButtonComp from "../components/PublicPagesReusable/ButtonComp";
import PteMaxNav from '../components/Frontend/PteMax/PteMaxNav';
import PteMaxFooter from '../components/Frontend/PteMax/PteMaxFooter';
// ---- Validation helpers (mirrored from ContactUs) ----
const EMAIL_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9._%+\-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
// Allow Unicode letters (\p{L}) + combining marks (\p{M}) so diacritics like
// é/ñ survive whether they're precomposed or decomposed, plus space, apostrophe, hyphen.
const NAME_DISALLOWED_REGEX = /[^\p{L}\p{M} '\-]/gu;
const NAME_ALLOWED_REGEX = /^[\p{L}\p{M} '\-]+$/u;

const normalizeName = (value) => (value || "").toString().normalize("NFC");

const validateField = (field, value) => {
  switch (field) {
    case "name": {
      const v = normalizeName(value).trim();
      if (!v) return "Name is required.";
      if (!NAME_ALLOWED_REGEX.test(v)) {
        return "Please use letters, spaces, apostrophes, or hyphens only.";
      }
      return "";
    }
    case "institute": {
      const v = (value || "").toString().trim();
      if (!v) return "Institute name is required.";
      return "";
    }
    case "email": {
      const v = (value || "").toString().trim();
      if (!v) return "Email is required.";
      if (!EMAIL_REGEX.test(v) || /\.\./.test(v.split("@")[0])) return "Please enter a valid email address.";
      return "";
    }
    case "phone": {
      const v = (value || "").toString().trim();
      if (!v) return "Phone Number is required.";
      if (!isValidPhoneNumber(v)) {
        return "Please enter a valid phone number for the selected country.";
      }
      return "";
    }
    case "address": {
      const v = (value || "").toString().trim();
      if (!v) return "Address is required.";
      return "";
    }
    case "city": {
      const v = (value || "").toString().trim();
      if (!v) return "City is required.";
      return "";
    }
    case "message":
      if (!(value || "").toString().trim()) return "Message is required.";
      return "";
    default:
      return "";
  }
};

// ---- Right-side contact info (unchanged content, just re-shaped for the card layout) ----
const FEATURES = [
  {
    icon: "fa fa-map-marker",
    title: "ADDRESS",
    body: (
      <a
        style={{ color: 'white' }}
        href="https://goo.gl/maps/JQHVgKAPkBA9oZKQ7"
        target="_blank"
        rel="noopener noreferrer"
      >
        Level 1, Suite 1 22 Horwood Place (10,586.39 km) 2150 Parramatta, New South Wales
      </a>
    ),
  },
  {
    icon: "fa fa-envelope-o",
    title: "EMAIL",
    body: (
      <a style={{ color: 'white' }} href="mailto:info@20task.com">
        info@20task.com
      </a>
    ),
  },
  {
    icon: "fa fa-phone",
    title: "PHONE",
    body: (
      <>
        <a style={{ color: 'white' }} href="tel:918360986507">
          +918360986507
        </a>
        <br />
        <a style={{ color: 'white' }} href="tel:918360829427">
          +918360829427
        </a>
      </>
    ),
  },
];

const TwentyTaskContact = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    institute: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [subdomain, setSubdomain] = useState(false);

  // AOS.init() ran in the constructor in the class version; runs once on mount here.
  useEffect(() => {
    AOS.init();
  }, []);

  // Redirect to auth/login when on a subdomain
  useEffect(() => {
    if (subdomain) {
      navigate('/auth/login');
    }
  }, [subdomain, navigate]);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      // clear the error as soon as the user starts fixing it
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleChange = (field) => (e) => setField(field, e.target.value);
  const handleNameChange = (e) =>
    setField("name", normalizeName(e.target.value).replace(NAME_DISALLOWED_REGEX, ""));
  const handleBlur = (field) => () => {
    const message = validateField(field, formData[field]);
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  };
  const handlePhoneChange = (value) => setField("phone", value);

  const validateAll = () => {
    const next = {};
    Object.keys(formData).forEach((field) => {
      const message = validateField(field, formData[field]);
      if (message) next[field] = message;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submitForm = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (submittingRef.current) return; // synchronous block — runs before re-render

    if (!validateAll()) return;

    submittingRef.current = true;
    setSubmitting(true);

    try {
      const data = await API({
        method: 'POST',
        url: "post/contact",
        data: {
          name: formData.name,
          institute: formData.institute,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          message: formData.message,
        },
      });

      setFormData({ name: '', institute: '', email: '', phone: '', address: '', city: '', message: '' });
      setErrors({});
      store.dispatch(setAlert({ 'flag': true, 'type': 1, 'message': data.data.response.message }));
    } catch {
      // leave field values intact so the user can retry
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <PteMaxNav />

        <div className="custom-container" style={{margin:"0px 34px"}}>
          <section className=" pte-consultation contact-us-section px-0 pt-5 custom-container">
            <div className="pte-consultation__container justify-content-center">

              <form className="pte-consultation__form contact-form-col" onSubmit={submitForm} noValidate>
                <div className="audio-transcript-text p-0 border-0 mt-0">
                  <p className="FS28 fw-600" style={{ color: "#19181C" }}>Send Us a Message</p>
                  <span className="FS15FW400">Fill out the form below and we'll get back to you within 24 hours.</span>
                  <hr />
                </div>

                <div className="pte-consultation__row gap-3">
                  <label className="pte-consultation__field">
                    <span className="pte-consultation__label">Full Name</span>
                    <input
                      type="text"
                      className="pte-consultation__input"
                      placeholder="Your name..."
                      value={formData.name}
                      onChange={handleNameChange}
                      onBlur={handleBlur("name")}
                      aria-invalid={!!errors.name}
                      maxLength={30}
                    />
                    {errors.name && <span className="pte-consultation__error">{errors.name}</span>}
                  </label>
                  <label className="pte-consultation__field">
                    <span className="pte-consultation__label">Institute Name</span>
                    <input
                      type="text"
                      className="pte-consultation__input"
                      placeholder="Your Institute name..."
                      value={formData.institute}
                      onChange={handleChange("institute")}
                      onBlur={handleBlur("institute")}
                      aria-invalid={!!errors.institute}
                    />
                    {errors.institute && <span className="pte-consultation__error">{errors.institute}</span>}
                  </label>
                </div>

                <div className="pte-consultation__row gap-3">
                  <label className="pte-consultation__field">
                    <span className="pte-consultation__label">Email Address</span>
                    <input
                      type="email"
                      className="pte-consultation__input"
                      placeholder="Your email..."
                      value={formData.email}
                      onChange={handleChange("email")}
                      onBlur={handleBlur("email")}
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <span className="pte-consultation__error">{errors.email}</span>}
                  </label>
                  <div className={`pte-consultation__field${errors.phone ? " pte-consultation__field--invalid" : ""}`}>
                    <span className="pte-consultation__label">Phone Number</span>
                    <CustomPhoneInput
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      onBlur={handleBlur("phone")}
                    />
                    {errors.phone && <span className="pte-consultation__error">{errors.phone}</span>}
                  </div>
                </div>

                <div className="pte-consultation__row gap-3">
                  <label className="pte-consultation__field">
                    <span className="pte-consultation__label">Address</span>
                    <input
                      type="text"
                      className="pte-consultation__input"
                      placeholder="Your address..."
                      value={formData.address}
                      onChange={handleChange("address")}
                      onBlur={handleBlur("address")}
                      aria-invalid={!!errors.address}
                    />
                    {errors.address && <span className="pte-consultation__error">{errors.address}</span>}
                  </label>
                  <label className="pte-consultation__field">
                    <span className="pte-consultation__label">City</span>
                    <input
                      type="text"
                      className="pte-consultation__input"
                      placeholder="Your city..."
                      value={formData.city}
                      onChange={handleChange("city")}
                      onBlur={handleBlur("city")}
                      aria-invalid={!!errors.city}
                    />
                    {errors.city && <span className="pte-consultation__error">{errors.city}</span>}
                  </label>
                </div>

                <label className="pte-consultation__field" style={{ flex: "none" }}>
                  <span className="pte-consultation__label">Your Message</span>
                  <textarea
                    className="pte-consultation__input"
                    placeholder="Message..."
                    value={formData.message}
                    onChange={handleChange("message")}
                    onBlur={handleBlur("message")}
                    aria-invalid={!!errors.message}
                    rows={4}
                    style={{ height: "auto", minHeight: "120px", resize: "vertical" }}
                  />
                  {errors.message && <span className="pte-consultation__error">{errors.message}</span>}
                </label>

                <div className={`pte-consultation__submit-wrap${submitting ? " pte-consultation__submit-wrap--disabled" : ""}`}
                 onMouseDown={(e) => e.preventDefault()}>
                   <button
                  className="primary-btn"
                  onClick={submitting ? () => {} : submitForm}
                  style={{ backgroundColor: "var(--la-secondary-2)" , boxShadow:"none" }}
                >
                 {submitting ? "Processing..." : "Submit"}
                  <img
                    src="/images/web-new-images/right-arrow.svg"
                    alt="PteMax"
                  />
                </button>
                </div>
              </form>

              <div className="custom-container contact-info-col p-0">
                <div className="master-card card3 border-0 d-flex flex-column gap-2 mx-0 addressArea contact-us-features">
                  <div>
                    <h1 className="FS28 fw-600 mb-2">Empower your Institute today</h1>
                    <h3>Ask Us Anything</h3>
                    <span className="FS15FW400 mb-5">
                      Feel free to contact us if you need some help, consultation or you have some other questions.
                    </span>
                  </div>
                  {FEATURES.map((item, index) => (
                    <React.Fragment key={index}>
                      <hr />
                      <div className="mapOuter d-flex gap-3">
                        <i className={item.icon} aria-hidden="true" style={{ marginTop: '6px' }}></i>
                        <div style={{ wordBreak: "break-word" }}>
                          <h3 className="FS16FW500 fw-600 mb-1">{item.title}</h3>
                          <p className="FS20FW500 mb-0">{item.body}</p>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>

            </div>
          </section>
        </div>
     
      <PteMaxFooter />
    </React.Fragment>
  );
};

export default TwentyTaskContact;